<template>
    <div>
        <breadcrumb class="px-4 bg-light pb-0" :items="breadcrumbs" />
        <div class="page-content">
            <div class="row">
                <div class="col-md-6 d-md-flex align-items-baseline">
                    <h1 class="mr-2">Labs</h1>
                </div>
                <div class="col-md-6 pb-3">
                    <input-search-icon
                        ref="searchInput"
                        @search="handleSearch"
                        placeholder="Search"
                        :debouncetime="500"
                    >
                        <template v-slot:before>
                            <dropdown
                                class="pr-2"
                                :btnContent="selectedProviderLabel"
                                :itemList="providerList"
                                @selected="changeProviderFilter"
                            ></dropdown>
                            <dropdown
                                class="pr-2"
                                :btnContent="selectedStatusLabel"
                                :itemList="statusList"
                                @selected="changeStatusFilter"
                            ></dropdown>
                        </template>
                    </input-search-icon>
                </div>
            </div>

            <div class="card rounded-lg p-3">
                <div
                    class="card-body p-0"
                    :class="{
                        'overflow-auto': !isLoading
                    }"
                >
                    <div class="table-holder table-responsive">
                        <LoaderList v-if="isLoading" class="w-100" />
                        <table
                            v-else
                            class="table list"
                            :class="{
                                'table-hover': list.length > 0
                            }"
                        >
                            <thead>
                                <tr>
                                    <th>
                                        <sorting
                                            text="Patient (ID):"
                                            :order-by="orderBy('patient')"
                                            @sorting="
                                                val => sort(val, 'patient')
                                            "
                                        >
                                        </sorting>
                                    </th>
                                    <th>
                                        <sorting
                                            text="Species:"
                                            :order-by="orderBy('species')"
                                            @sorting="
                                                val => sort(val, 'species')
                                            "
                                        >
                                        </sorting>
                                    </th>
                                    <th>
                                        <sorting
                                            text="Test:"
                                            :order-by="orderBy('test')"
                                            @sorting="val => sort(val, 'test')"
                                        >
                                        </sorting>
                                    </th>
                                    <th>
                                        <sorting
                                            text="Test Date:"
                                            :order-by="orderBy('testDate')"
                                            @sorting="
                                                val => sort(val, 'testDate')
                                            "
                                        >
                                        </sorting>
                                    </th>
                                    <th>
                                        <sorting
                                            text="Result Date:"
                                            :order-by="orderBy('resultDate')"
                                            @sorting="
                                                val => sort(val, 'resultDate')
                                            "
                                        >
                                        </sorting>
                                    </th>
                                    <th>
                                        Lab Vendor:
                                    </th>
                                    <th>Lab Status:</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody v-if="list.length > 0">
                                <tr v-for="record in list" :key="record.id">
                                    <td>
                                        <span
                                            v-if="
                                                filter.status === 'Unassigned'
                                            "
                                        >
                                            <router-link
                                                v-if="
                                                    record.patient &&
                                                    record.patient.encodedId
                                                "
                                                :to="{
                                                    name: 'patient_profile',
                                                    params: {
                                                        id:
                                                            record.patient
                                                                .encodedId
                                                    }
                                                }"
                                            >
                                                {{ record.patient_name }}
                                                <span
                                                    v-if="record.patient.index"
                                                >
                                                    ({{ record.patient.index }})
                                                </span>
                                            </router-link>
                                            <span v-else>
                                                {{ record.patient_name }}
                                                <span
                                                    v-if="
                                                        record.patient &&
                                                        record.patient.index
                                                    "
                                                >
                                                    ({{ record.patient.index }})
                                                </span>
                                            </span>
                                        </span>
                                        <span v-else>
                                            <router-link
                                                v-if="
                                                    record.patient &&
                                                    record.patient.encoded_id
                                                "
                                                :to="{
                                                    name: 'patient_profile',
                                                    params: {
                                                        id:
                                                            record.patient
                                                                .encoded_id
                                                    }
                                                }"
                                            >
                                                {{
                                                    record.patient
                                                        ? record.patient.name
                                                        : ""
                                                }}
                                                <span
                                                    v-if="record.patient.index"
                                                >
                                                    ({{ record.patient.index }})
                                                </span>
                                            </router-link>
                                            <span v-else>
                                                {{
                                                    record.patient
                                                        ? record.patient.name
                                                        : ""
                                                }}
                                                <span
                                                    v-if="
                                                        record.patient &&
                                                        record.patient.index
                                                    "
                                                >
                                                    ({{ record.patient.index }})
                                                </span>
                                            </span>
                                        </span>
                                    </td>
                                    <td class="text-capitalize">
                                        {{ properPatientSpecies(record) }}
                                    </td>
                                    <td>
                                        <span
                                            v-if="
                                                filter.status === 'Unassigned'
                                            "
                                        >
                                            <test-name-unassigned
                                                :record="record"
                                            />
                                        </span>
                                        <span v-else>
                                            <test-name :record="record" />
                                        </span>
                                    </td>
                                    <td>
                                        {{ properTestDate(record) }}
                                    </td>
                                    <td>
                                        {{ properResultDate(record) }}
                                    </td>
                                    <td>
                                        {{ record.source }}
                                    </td>
                                    <td>
                                        <span
                                            v-if="
                                                filter.status === 'Unassigned'
                                            "
                                            >{{ record.status || "-" }}</span
                                        >
                                        <span v-else>
                                            {{ record.lab_status || "-" }}
                                        </span>
                                    </td>
                                    <td>
                                        <div
                                            class="small text-nowrap options"
                                            v-if="
                                                filter.status === 'Unassigned'
                                            "
                                        >
                                            <span
                                                v-if="
                                                    _hasAccess(
                                                        'patient.unassigned.labs.preview.result'
                                                    )
                                                "
                                                @click="previewResult(record)"
                                            >
                                                Preview Result
                                            </span>
                                            <span>|</span>
                                            <span @click="assignLab(record)">
                                                Assign
                                            </span>
                                            <span>|</span>
                                            <span @click="deleteLab(record)">
                                                Delete
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr>
                                    <td colspan="8">
                                        <i>No records available</i>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <b-pagination
                    :disabled="isLoading"
                    class="vet-pagination"
                    align="right"
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    prev-text="Prev"
                    next-text="Next"
                    first-text="First"
                    last-text="Last"
                    @change="changePage"
                ></b-pagination>
            </div>
        </div>

        <modal-delete
            ref="modalDelete"
            :reloadAfter="true"
            @reloadlist="fetchList(1)"
        />

        <modal-assign
            ref="modalAssign"
            :reloadAfter="true"
            @reloadlist="fetchList(1)"
        />

        <modal-assign-onprem
            ref="modalAssignOnprem"
            :reloadAfter="true"
            @reload="fetchList(1)"
        />

        <modal-assign-abaxisapi
            ref="modalAssignAbaxisapi"
            :reloadAfter="true"
            @reload="fetchList(1)"
        />

        <modal-assign-lab
            ref="modalAssignLab"
            :reloadAfter="true"
            @reload="fetchList(1)"
        />

        <modal-assign-antech
            ref="modalAssignAntech"
            :reloadAfter="true"
            @reload="fetchList(1)"
        />

        <modal-preview-result ref="modalPreviewResult" @assign="assignLab" />
    </div>
</template>

<script>
import Breadcrumb from "@/components/commons/breadcrumb.vue";
import InputSearchIcon from "@/module/commons/input-search-icon.vue";
import Dropdown from "@/components/commons/dropdown.vue";
import LoaderList from "@/components/elements/loader-list.vue";
import Sorting from "@/module/commons/sorting.vue";
import ModalDelete from "@/module/patient/dashboard/unassigned-labs/modal-delete";
import ModalAssign from "@/module/patient/dashboard/unassigned-labs/modal-assign";
import ModalAssignLab from "@/components/unassign-labs/assign-lab";
import ModalAssignAntech from "@/components/unassign-labs/assign-antech";
import ModalAssignOnprem from "@/components/unassign-labs/assign-onprem";
import ModalAssignAbaxisapi from "@/components/unassign-labs/assign-abaxisapi";
import ModalPreviewResult from "@/components/unassign-labs/preview-result";
import TestNameUnassigned from "./test-name-unassigned.vue";
import TestName from "./test-name.vue";

const columnMapping = {
    patient: "sortPatient",
    species: "sortSpecies",
    test: "sortTest",
    testDate: "sortTestDate",
    resultDate: "sortResultDate",
    labProvider: "sortLabProvider"
};

const UNASSIGNED = "unassigned";

const labVendor = {
    ONPREM: "onprem",
    LAB: "lab",
    IDEXX: "idexx",
    ZNLABS: "znlabs",
    MIDWEST: "midwest",
    NBIOVET: "nbiovet",
    VETEK: "vetek",
    MICROVET: "microvet",
    BIONOTE: "bionote",
    HESKA: "heska",
    ABAXIS: "abaxis",
    HESKA_CLOUD: "heska_cloud",
    ABAXIS_FUSE: "abaxis_fuse",
    ABAXIS_API: "abaxis_api",
    ANTECH: "antech"
};

const statuses = ["Unassigned", "Not Sent", "Completed", "Pending", "Expired"];

export default {
    components: {
        Breadcrumb,
        InputSearchIcon,
        Dropdown,
        LoaderList,
        Sorting,
        ModalDelete,
        ModalAssign,
        ModalAssignLab,
        ModalAssignAntech,
        ModalAssignOnprem,
        ModalAssignAbaxisapi,
        ModalPreviewResult,
        TestNameUnassigned,
        TestName
    },
    data() {
        return {
            filter: {
                search: "",
                type: "all",
                status: this.capitalize(UNASSIGNED)
            },
            sorting: {
                column: "",
                order: null
            },
            isLoading: false,
            list: [],
            currentPage: 1,
            selectedProviderLabel: "All Vendors",
            providerList: [],
            totalRows: 0,
            perPage: 10,
            abortController: null,
            selectedStatusLabel: "",
            statusList: []
        };
    },
    computed: {
        breadcrumbs() {
            return [
                {
                    text: "Patient",
                    to: {
                        name: "patient_dashboard"
                    }
                },
                {
                    text: "Labs",
                    href: "#"
                }
            ];
        }
    },
    async mounted() {
        if (!this._setting?.business?.id) {
            await this._getSetting();
        }
        /** TODO: Remove initStatuses() and enable fetchStatus()
         ** when fetchStatus endpoint has been improvised
         */
        // this.fetchStatus();
        this.initStatuses();
    },
    filters: {
        capitalize(value) {
            if (!value) return "";
            return value.charAt(0).toUpperCase() + value.slice(1);
        }
    },
    methods: {
        initStatuses() {
            this.statusList = statuses.map(status => ({
                label: status,
                value: {
                    count: 0,
                    status: status
                }
            }));

            this.changeStatusFilter(this.statusList[0]);
        },
        async fetchStatus() {
            this.isLoading = true;

            try {
                const res = await this.$http.get(
                    `${process.env.BARRAMUNDI}lab/lab-record-status/${this._setting.business.id}`,
                    {
                        params: {
                            source: this.filter.type || "all"
                        }
                    }
                );
                if (res?.data?.response?.code === 200) {
                    const status = res.data.response.resources;
                    this.statusList = status.map(status => ({
                        label: `${status.status} (${status.count})`,
                        value: status
                    }));
                    this.changeStatusFilter(this.statusList[0]);
                }
            } catch (error) {
                this.isLoading = false;
            }
        },
        async fetchList(page) {
            this.isLoading = true;

            this.cancelFetch(); // cancel current fetch, if any
            this.abortController = new AbortController();

            const sortKey = columnMapping[this.sorting.column];
            const sortOption = sortKey
                ? { [sortKey]: this.sorting.order || "asc" }
                : {};

            let searchOption = {};
            if (this.filter.search !== "") {
                searchOption = {
                    search: this.filter.search
                };
            }

            try {
                let res = null;
                if (this.filter.status === this.capitalize(UNASSIGNED)) {
                    res = await this.$http.get(
                        `${process.env.BARRAMUNDI}lab/unassign-lab/${this._setting.business.id}`,
                        {
                            params: {
                                type: this.filter.type || "all",
                                page: page || 1,
                                ...sortOption,
                                ...searchOption
                            },
                            signal: this.abortController.signal
                        }
                    );
                } else {
                    res = await this.$http.get(
                        `${process.env.BARRAMUNDI}lab/lab-record/${this._setting.business.id}`,
                        {
                            params: {
                                source: this.filter.type || "all",
                                status:
                                    this.filter.status ||
                                    this.capitalize(UNASSIGNED),
                                page: page || 1,
                                ...sortOption,
                                ...searchOption
                            },
                            signal: this.abortController.signal
                        }
                    );
                }

                if (res?.data?.response?.message?.code === 200) {
                    const {
                        resources,
                        meta,
                        lab_providers = []
                    } = res.data.response;
                    this.list = resources;

                    this.totalRows = meta.total;
                    this.perPage = meta.perPage;
                    this.currentPage = page;

                    this.providerList = lab_providers?.map(lab => ({
                        label: lab.replaceAll("_", " "),
                        value: lab
                    }));
                    this.providerList.unshift({
                        label: "All Vendors",
                        value: "all"
                    });
                } else {
                    console.error(res?.data?.message);
                }

                this.isLoading = false;
                this.abortController = null;
            } catch (error) {
                if (error?.message !== "canceled") {
                    this.isLoading = false;
                }
            }
        },
        properPatientSpecies(record) {
            let species = "";

            if (record.patient && Object.keys(record.patient).length > 0) {
                if (this.filter.status === this.capitalize(UNASSIGNED)) {
                    species = record.patient.species;
                } else {
                    species = record.patient.patient_species
                        ? record.patient.patient_species.value
                        : "";
                }
            }

            return typeof species === "string" ? species.toLowerCase() : "";
        },
        sort(value, column) {
            this.sorting = {
                column: column,
                order: value
            };

            this.fetchList(1);
        },
        orderBy(column) {
            if (this.sorting.column === column) {
                return this.sorting.order;
            }
            return null;
        },
        changeProviderFilter({ label, value }) {
            this.filter.type = value;
            this.selectedProviderLabel = label;
            this.filter.search = "";
            this.$refs.searchInput.resetSearch();

            setTimeout(() => {
                // TODO: Enable when fetchStatus endpoint has been improved
                // this.fetchStatus();
            }, 100);
        },
        changeStatusFilter({ label, value }) {
            this.filter.status = value.status;
            this.selectedStatusLabel = label.split("(")[0];

            setTimeout(() => {
                this.fetchList(1);
            }, 100);
        },
        handleSearch(keyword = "") {
            this.filter.search = keyword;
            setTimeout(() => {
                this.fetchList(1);
            }, 100);
        },
        properTestDate(record) {
            let testDate = "";
            if (this.filter.status === this.capitalize(UNASSIGNED)) {
                switch (record.module) {
                    case labVendor.ONPREM:
                        if (record.test_date) {
                            testDate = this._setDateTimeDisplay(
                                record.test_date
                            );
                        }
                        break;

                    case labVendor.IDEXX:
                    case labVendor.ZNLABS:
                    case labVendor.MIDWEST:
                    case labVendor.NBIOVET:
                    case labVendor.VETEK:
                    case labVendor.MICROVET:
                    case labVendor.BIONOTE:
                    case labVendor.HESKA:
                    case labVendor.ABAXIS:
                    case labVendor.HESKA_CLOUD:
                        if (record.order_date) {
                            testDate = this._setDateTimeDisplay(
                                record.order_date,
                                record.module == labVendor.IDEXX ? false : true
                            );
                        }
                        break;

                    case labVendor.ANTECH:
                        if (record.order_received_datetime) {
                            testDate = this._setDateTimeDisplay(
                                record.order_received_datetime
                            );
                        }
                        break;
                    default:
                        break;
                }
            } else {
                switch (record.source) {
                    case labVendor.ONPREM.toUpperCase():
                        if (record.ordered_date) {
                            testDate = this._setDateTimeDisplay(
                                record.ordered_date.date
                            );
                        }
                        break;

                    case labVendor.IDEXX.toUpperCase():
                    case labVendor.ZNLABS.toUpperCase():
                    case labVendor.MIDWEST.toUpperCase():
                    case labVendor.NBIOVET.toUpperCase():
                    case labVendor.VETEK.toUpperCase():
                    case labVendor.MICROVET.toUpperCase():
                    case labVendor.BIONOTE.toUpperCase():
                    case labVendor.HESKA.toUpperCase():
                    case labVendor.ABAXIS.toUpperCase():
                    case labVendor.HESKA_CLOUD.toUpperCase():
                        if (record.ordered_date) {
                            testDate = this._setDateTimeDisplay(
                                record.ordered_date.date,
                                record.source == labVendor.IDEXX.toUpperCase()
                                    ? false
                                    : true
                            );
                        }
                        break;

                    case labVendor.ANTECH.toUpperCase():
                        if (record.ordered_date) {
                            testDate = this._setDateTimeDisplay(
                                record.ordered_date.date
                            );
                        }
                        break;
                    default:
                        if (record.ordered_date) {
                            testDate = this._setDateTimeDisplay(
                                record.ordered_date.date
                            );
                        }
                        break;
                }
            }
            return testDate;
        },
        properResultDate(record) {
            let resultDate = "";
            if (this.filter.status === this.capitalize(UNASSIGNED)) {
                switch (record.module) {
                    case labVendor.ONPREM:
                        break;
                    case labVendor.ABAXIS_API:
                    case labVendor.ABAXIS_FUSE:
                        if (record.result_date) {
                            resultDate = this._setDateTimeDisplay(
                                record.result_date
                            );
                        }
                        break;
                    case labVendor.LAB:
                        if (record.result_date) {
                            resultDate = this._setDateTimeDisplay(
                                record.result_date
                            );
                        }
                        break;

                    case labVendor.IDEXX:
                    case labVendor.ZNLABS:
                    case labVendor.MIDWEST:
                    case labVendor.NBIOVET:
                    case labVendor.VETEK:
                    case labVendor.MICROVET:
                    case labVendor.BIONOTE:
                    case labVendor.HESKA:
                    case labVendor.ABAXIS:
                    case labVendor.HESKA_CLOUD:
                        if (record.result_received) {
                            resultDate = this._setDateTimeDisplay(
                                record.result_received
                            );
                        }
                        break;

                    case labVendor.ANTECH:
                        if (record.latest_result_received_datetime) {
                            resultDate = this._setDateTimeDisplay(
                                record.latest_result_received_datetime
                            );
                        }
                        break;
                    default:
                        break;
                }
            } else {
                switch (record.source) {
                    case labVendor.ONPREM.toUpperCase():
                        break;
                    case labVendor.ABAXIS_API.toUpperCase():
                    case labVendor.ABAXIS_FUSE.toUpperCase():
                        if (record.received_date) {
                            resultDate = this._setDateTimeDisplay(
                                record.received_date.date
                            );
                        }
                        break;
                    case labVendor.LAB.toUpperCase():
                        if (record.received_date) {
                            resultDate = this._setDateTimeDisplay(
                                record.received_date.date
                            );
                        }
                        break;

                    case labVendor.IDEXX.toUpperCase():
                    case labVendor.ZNLABS.toUpperCase():
                    case labVendor.MIDWEST.toUpperCase():
                    case labVendor.NBIOVET.toUpperCase():
                    case labVendor.VETEK.toUpperCase():
                    case labVendor.MICROVET.toUpperCase():
                    case labVendor.BIONOTE.toUpperCase():
                    case labVendor.HESKA.toUpperCase():
                    case labVendor.ABAXIS.toUpperCase():
                    case labVendor.HESKA_CLOUD.toUpperCase():
                        if (record.received_date) {
                            resultDate = this._setDateTimeDisplay(
                                record.received_date.date
                            );
                        }
                        break;

                    case labVendor.ANTECH.toUpperCase():
                        if (record.received_date) {
                            resultDate = this._setDateTimeDisplay(
                                record.received_date.date
                            );
                        }
                        break;
                    default:
                        if (record.received_date) {
                            resultDate = this._setDateTimeDisplay(
                                record.received_date.date
                            );
                        }
                        break;
                }
            }
            return resultDate;
        },
        previewResult(record) {
            this.$refs.modalPreviewResult.open(record);
        },
        assignLab(record) {
            switch (record.module) {
                case labVendor.ONPREM:
                    this.$refs.modalAssignOnprem.open(record);
                    break;
                case labVendor.ABAXIS_API:
                    this.$refs.modalAssignAbaxisapi.open(record);
                    break;
                case labVendor.LAB:
                case labVendor.IDEXX:
                case labVendor.ZNLABS:
                case labVendor.MIDWEST:
                case labVendor.NBIOVET:
                case labVendor.VETEK:
                case labVendor.MICROVET:
                case labVendor.BIONOTE:
                case labVendor.HESKA:
                case labVendor.HESKA_CLOUD:
                    this.$refs.modalAssignLab.open(record);
                    break;
                case labVendor.ANTECH:
                    this.$refs.modalAssignAntech.open(record);
                    break;
            }
        },
        deleteLab(record) {
            this.$refs.modalDelete.open({
                recordId: record.id.toString(),
                recordModule: record.module,
                businessId: this._setting.business.id
            });
        },
        changePage(page) {
            this.fetchList(page);
        },
        cancelFetch() {
            if (this.abortController) {
                this.abortController.abort();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
td .options {
    display: flex;
    justify-content: end;
    gap: 5px;

    span {
        cursor: pointer;
    }
}
</style>
